import makeApiCall from "./master/axiosIndex";

// Site daily capture — the Material-Stores and Mechanical day sheets on the
// Daily Execution page. Same wire rules as ActivityExecution.tsx: every decimal
// is a STRING, '' means "not set" and is never a fake '0', and gRPC omits
// false / 0 / '' fields entirely, so every read goes through a normaliser that
// puts the defaults back before a component sees the data.

export type DaySheetScope = "MONITORED" | "ALL";

export interface DaySheetTask {
  taskId: number;
  // The unique identity ('AE-0101/B3.1.1(1)') — never internalTaskNumber.
  taskSequenceId: string;
  taskName: string;
  workQuantity: string;
  workUomName: string;
  // '' = nothing produced that day (no execution captured for the face).
  producedQty: string;
  lineCount: number;
  recordedCount: number;
  hasEntryForDate: boolean;
  isMonitored: boolean;
  // Material sheet: materials still owed to this task's civil work up to the
  // date (material often arrives a day after the work). No dates by design —
  // the user books the task, the server decides which work date it covers.
  pendingMaterialCount: number;
  hasPendingMaterial: boolean;
  // The same for machine hours and the Mucking / PRW cost heads.
  pendingEquipmentCount: number;
  pendingCostHeadCount: number;
  // Σ civil production up to and including the date; '' = none.
  producedTillDate: string;
  // Material sheet: the civil dates still owed material, oldest first.
  // lineCount = materials still owed to the date; bookedTodayCount = materials
  // the sheet date's own issue booked against it (kept so it can be corrected).
  pendingMaterialDates: Array<{ workDate: string; producedQty: string; lineCount: number; bookedTodayCount: number }>;
  // Equipment sheet: the same for machine hours (lineCount = machines).
  pendingEquipmentDates: Array<{ workDate: string; producedQty: string; lineCount: number; bookedTodayCount: number }>;
  // Material sheet (Mucking & PRW tab): the same for the cost heads (lineCount = norm lines).
  pendingCostHeadDates: Array<{ workDate: string; producedQty: string; lineCount: number; bookedTodayCount: number }>;
}

export interface DaySheetActivity {
  activityId: number;
  activityName: string;
  activitySequenceId: string;
  taskCount: number;
  recordedTaskCount: number;
  tasks: DaySheetTask[];
}

export interface DaySheetPhase {
  phaseId: number; // 0 = no phase
  phaseName: string;
  taskCount: number;
  recordedTaskCount: number;
  activities: DaySheetActivity[];
}

export interface DaySheet {
  projectId: number;
  date: string;
  // The scope the server ACTUALLY applied — ALL when tracking is not
  // configured, whatever was asked for.
  scope: DaySheetScope;
  monitoringConfigured: boolean;
  taskCount: number;
  recordedTaskCount: number;
  // DPR submitted for the day — the sheet is read-only.
  dayLocked: boolean;
  phases: DaySheetPhase[];
}

export type RateSource = "MOVING_AVG" | "NORM" | "MASTER" | "ENTERED" | "";

export interface MaterialLine {
  taskBoqId: number;
  materialId: number;
  materialName: string;
  productCode: string; // '' when the material has none
  // supplier_category_master via buyer_materials.category. 0 / '' is the real
  // "uncategorised" bucket the sheet groups last, not a missing value.
  categoryId: number;
  categoryName: string;
  uomName: string;
  uomId: number;
  allottedQuantity: string;
  qtyIssuedToday: string; // '' = none
  qtyIssuedTillDate: string;
  remarks: string;
  rate: string; // '' = none stored
  rateSource: RateSource;
  suggestedRate: string;
  suggestedRateSource: RateSource;
  // The TARGET rate: the rate frozen on the task's norm application. '' when the
  // norm carries no price — then the line has a target quantity but no target
  // amount, and the sheet shows '—' rather than a zero.
  normRate: string;
  normQtyPerUnit: string; // '' = no plan
  targetQtyToday: string; // '' when nothing produced
  /** The norm's PUC factor; '' on a BOQ line with no norm behind it. */
  pucFactor: string;
  /** The norm's wastage as the stored FRACTION — 0.02 is 2%. */
  wastage: string;
  // Requirement from the task's civil work up to the date not yet booked
  // (material's master unit). '' = nothing pending / no norm.
  pendingQty: string;
  // pendingQty split by the civil date it is owed to, oldest first.
  pendingByDate: MaterialPendingDay[];
  // EVERY civil day (booked ones too), oldest first, with what this date's own
  // issue booked on each — what the date-wise view shows and corrects.
  civilDays: MaterialCivilDay[];
  // Against civil done TILL DATE: norm x that, and Σ qty x rate issued up to
  // the date ('' = no issue carried a rate).
  targetQtyTillDate: string;
  amountIssuedTillDate: string;
}

export interface MaterialCivilDay {
  workDate: string;
  producedQty: string;
  requiredQty: string;
  bookedQty: string; // every issue up to the date
  bookedTodayQty: string; // of which this date's own issue
  pendingQty: string;
  // Σ booked qty x the rate of the issue it came from ('' = none priced), and
  // the qty that covers.
  bookedAmount: string;
  bookedPricedQty: string;
  // Equipment only: consumed cost heads of EARLIER logs booked on this date
  // (each log's heads x hours booked here / the log's hours).
  earlierHeads?: Array<{
    head: string;
    qty: string;
    uomName: string;
    rate: string;
    amount: string;
    logDate: string;
    hoursHere: string;
    logHours: string;
  }>;
}

export interface MaterialPendingDay {
  workDate: string; // YYYY-MM-DD
  producedQty: string; // task work unit
  requiredQty: string;
  pendingQty: string;
}

export interface MaterialSheet {
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  issueDate: string;
  producedQty: string;
  // Σ civil production up to and including the date ('' = none).
  producedTillDate: string;
  workUomName: string;
  hasEntryForDate: boolean;
  lines: MaterialLine[];
}

// ---- the day rolled up by material category (target vs actual) -----------
// Amounts are decimal strings and '' means "nothing to state" — no norm price,
// or nothing issued — never a zero.
export interface MaterialCategoryTotal {
  categoryId: number; // 0 = uncategorised
  categoryName: string;
  lineCount: number;
  taskCount: number;
  targetAmount: string;
  actualAmount: string;
  // Set only where one work unit covers the scope: amounts add across tasks,
  // per-unit figures do not. Always set on a task (one task, one unit).
  targetPerUnit: string;
  actualPerUnit: string;
}

export interface MaterialTaskCategories {
  taskId: number;
  categories: MaterialCategoryTotal[];
  targetAmount: string;
  actualAmount: string;
  producedQty: string;
  workUomName: string;
}

export interface MaterialCategorySummary {
  projectId: number;
  date: string;
  scope: DaySheetScope;
  monitoringConfigured: boolean;
  taskCount: number;
  categories: MaterialCategoryTotal[];
  tasks: MaterialTaskCategories[];
  targetAmount: string;
  actualAmount: string;
  // Both '' when the day's tasks span more than one work unit — then the strip
  // shows ₹ totals and says why there is no per-unit figure.
  producedQty: string;
  perUnitWorkUomName: string;
}

// ---- the project store's daily priced ledger ------------------------------
// Every decimal is a string and '' means "nothing to state", never a zero: an
// unpriced balance has no rate, and a material the tasks never issued has no
// variance rather than a variance of 0.
//
// The identity every row keeps:
//   closingQty   = openingQty   + purchasedQty   + adjustmentQty   - issuedQty
//   closingValue = openingValue + purchasedValue + adjustmentValue - issuedValue
// Issues and adjustments are valued at the day's weighted average cost, which is
// why the value side always ties.

// Issuing LESS than allocated is not flagged — a norm is a ceiling against
// output that may still be in progress — but the figure is shown either way.
export type StockTargetState = "OVER" | "WITHIN" | "NO_TARGET" | "";

export type StockVarianceState =
  | "MATCH"
  | "STORE_HIGHER"
  | "STORE_LOWER"
  | "NO_TASK_ISSUE"
  | "UNIT_MISMATCH"
  | "";

export interface StockRow {
  materialId: number;
  materialName: string;
  productCode: string;
  categoryId: number; // 0 = uncategorised (a real bucket, not missing)
  categoryName: string;
  uomId: number;
  uomName: string;
  // The day the opening was carried from — '' when nothing carried in, which is
  // exactly when the opening may be typed.
  carriedFrom: string;
  isOpeningAnchor: boolean;
  openingQty: string;
  openingRate: string;
  openingValue: string;
  purchasedQty: string;
  purchasedRate: string;
  purchasedValue: string;
  issuedQty: string; // the STORE's own figure, not the tasks'
  // What the store states it issued at. Recorded, not applied: issuedValue stays
  // at the day's weighted average cost, which is what makes the value side tie.
  issuedRate: string;
  issuedValue: string;
  adjustmentQty: string; // signed: damage / transfer out negative
  adjustmentValue: string;
  adjustmentReason: string;
  remarks: string;
  avgRate: string;
  closingQty: string;
  closingValue: string;
  // What the tasks booked that day (daily_material_issues), summed per unit.
  // Reference only — it never feeds the stock chain.
  taskIssuedQty: string;
  taskIssuedAmount: string;
  taskIssuedRate: string;
  varianceQty: string; // '' when the two cannot be compared
  varianceState: StockVarianceState;
  hasRow: boolean; // false = no movement stored for this day
  // The NORMS' price for the material, and whether the day's receipt beat it.
  // Every target costed on this material uses the norm rate, so paying more
  // understates them all.
  normRate: string;
  purchaseAboveNorm: boolean;
  // What the day's norms ALLOCATED to the tasks that actually produced. '' when
  // no task carrying this material produced anything that day.
  targetQty: string;
  targetTaskCount: number;
  targetState: StockTargetState;
}

export interface StockCategoryTotal {
  categoryId: number;
  categoryName: string;
  materialCount: number;
  movementCount: number;
  openingValue: string;
  purchasedValue: string;
  issuedValue: string;
  adjustmentValue: string;
  closingValue: string;
}

export interface StockSheet {
  projectId: number;
  date: string;
  dayLocked: boolean;
  materialCount: number;
  movementCount: number;
  mismatchCount: number;
  overNormCount: number;
  overTargetCount: number;
  negativeCount: number;
  openingValue: string;
  purchasedValue: string;
  issuedValue: string;
  adjustmentValue: string;
  closingValue: string;
  categories: StockCategoryTotal[];
  rows: StockRow[];
}

// ---- the same ledger read day by day over a range ------------------------
// Only days that HAVE movement appear: a day with no row carried its balance,
// and a row per material per calendar day would be mostly nothing.
export interface StockRegisterRow {
  materialId: number;
  materialName: string;
  productCode: string;
  categoryId: number;
  categoryName: string;
  uomName: string;
  openingQty: string;
  openingValue: string;
  purchasedQty: string;
  purchasedRate: string;
  purchasedValue: string;
  issuedQty: string;
  issuedRate: string;
  issuedValue: string;
  adjustmentQty: string;
  adjustmentValue: string;
  adjustmentReason: string;
  closingQty: string;
  closingValue: string;
  avgRate: string;
  remarks: string;
  // The checks. None is stored — they are recomputed on read, the same way the
  // day sheet computes them.
  normRate: string;
  purchaseAboveNorm: boolean;
  targetQty: string;
  targetTaskCount: number;
  targetState: StockTargetState;
  taskIssuedQty: string;
  varianceQty: string;
  varianceState: StockVarianceState;
}

export interface StockRegisterDay {
  date: string;
  materialCount: number;
  overNormCount: number;
  overTargetCount: number;
  mismatchCount: number;
  negativeCount: number;
  openingValue: string;
  purchasedValue: string;
  issuedValue: string;
  adjustmentValue: string;
  closingValue: string;
  rows: StockRegisterRow[];
}

export interface StockRegister {
  projectId: number;
  fromDate: string;
  toDate: string;
  dayCount: number;
  rowCount: number;
  // Flows add across days; balances do not — closingValue is the LAST moving
  // day's, not a sum.
  purchasedValue: string;
  issuedValue: string;
  adjustmentValue: string;
  closingValue: string;
  overNormCount: number;
  overTargetCount: number;
  mismatchCount: number;
  negativeCount: number;
  days: StockRegisterDay[];
}

export interface StockSaveRow {
  materialId: number;
  openingQty?: string;
  openingRate?: string;
  purchasedQty?: string;
  purchasedRate?: string;
  issuedQty?: string;
  issuedRate?: string;
  adjustmentQty?: string;
  adjustmentReason?: string;
  remarks?: string;
}

export interface MaterialSaveLine {
  materialId: number;
  taskBoqId?: number; // omit when 0 / null
  qtyIssued: string;
  remarks?: string;
  uomId?: number;
  rate?: string; // '' = not entered
  rateSource?: RateSource; // 'ENTERED' when typed/changed, else the suggested source
  // How much of qtyIssued was entered against which civil date — booked on
  // that date exactly.
  civilDates?: Array<{ civilDate: string; qty: string }>;
}

export const EQUIPMENT_HEAD_KEYS = [
  "depreciation",
  "interest",
  "repair",
  "misc",
  "insurance",
  "electricity",
  "fuel",
  "lubricants",
] as const;
export type EquipmentHeadKey = (typeof EQUIPMENT_HEAD_KEYS)[number];
// Each '' = not recorded (server treats blank as "same as working hours";
// an explicit '0' = head not applicable).
export type EquipmentHeadHours = Record<EquipmentHeadKey, string>;

// The web Mechanical model (money crosses this wire by the user's decision):
// fixed heads are the rate card's ₹/hr × hours worked, read-only; consumed
// heads are captured as actual qty × rate in a unit from the UOM master.
export const FIXED_HEAD_KEYS = ["depreciation", "interest", "insurance"] as const;
export type FixedHeadKey = (typeof FIXED_HEAD_KEYS)[number];
export const VARIABLE_HEAD_KEYS = ["repair", "misc", "electricity", "fuel", "lubricants"] as const;
export type VariableHeadKey = (typeof VARIABLE_HEAD_KEYS)[number];

export interface EquipmentFixedHead {
  head: FixedHeadKey;
  label: string;
  ratePerHour: string; // '' when the asset has no rate card on the date
  amount: string; // stored hoursWorked × ratePerHour; '' when either is absent
}

export interface EquipmentVariableHead {
  head: VariableHeadKey;
  label: string;
  qty: string;
  uomId: number;
  uomName: string;
  rate: string;
  amount: string;
  rateSource: "ENTERED" | "MASTER" | "";
  defaultUomId: number;
  defaultUomName: string;
  defaultRate: string;
  // The rate card's ₹ per machine-HOUR for this head (the norm); '' = none.
  normRatePerHour: string;
}

export interface UomOption {
  uomId: number;
  uomName: string;
  uomShortCode: string;
}

export interface EquipmentHeadInput {
  head: VariableHeadKey;
  qty: string;
  uomId: number;
  rate: string;
}

export interface EquipmentLine {
  assetId: number;
  assetCode: string;
  assetName: string;
  toolTypeName: string;
  plannedHours: string; // target hrs for today's output, '' when unknown
  // Hours still owed to the task's civil work already done; '' = none.
  pendingHours: string;
  // The norm's hours per unit x civil done TILL DATE ('' = no norm / no civil).
  targetHoursTillDate: string;
  // Every civil day with its target hours, booked hours (and of those, what
  // this date's log entered) and still owed — the date-wise view.
  civilDays: MaterialCivilDay[];
  hoursWorked: string;
  fuelLitres: string;
  readingStart: string;
  readingEnd: string;
  isBreakdown: boolean;
  idleReason: string;
  hoursTillDate: string;
  lastReadingEnd: string;
  prodNormPerHour: string; // '500' → "500 Cum/Hr" with prodNormUomName
  /** The deployment norm's three factors; '' when the asset has no norm. */
  nonProdFactor: string;
  utilizationFactor: string;
  applicationFactor: string;
  prodNormUomName: string;
  normLineCount: number;
  headHours: EquipmentHeadHours;
  fixedHeads: EquipmentFixedHead[];
  variableHeads: EquipmentVariableHead[];
  rateStatus: "" | "NO_RATE";
}

export interface EquipmentSheet {
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  logDate: string;
  producedQty: string;
  // Σ civil production up to and including the date ('' = none).
  producedTillDate: string;
  workUomName: string;
  hasEntryForDate: boolean;
  lines: EquipmentLine[];  uomOptions: UomOption[];
}

export interface EquipmentSaveLine {
  assetId: number;
  hoursWorked: string;
  fuelLitres: string;
  readingStart: string;
  readingEnd: string;
  isBreakdown: boolean;
  idleReason: string;
  headHours: EquipmentHeadHours;  heads?: EquipmentHeadInput[];
  // How many of hoursWorked were entered against which civil date.
  civilDates?: Array<{ civilDate: string; qty: string }>;
}

// ------------------------------------------------------------ normalisers
const str = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);
const num = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const bool = (value: unknown): boolean => value === true;

const normaliseTask = (raw: any): DaySheetTask => ({
  taskId: num(raw?.taskId),
  taskSequenceId: str(raw?.taskSequenceId),
  taskName: str(raw?.taskName),
  workQuantity: str(raw?.workQuantity),
  workUomName: str(raw?.workUomName),
  producedQty: str(raw?.producedQty),
  lineCount: num(raw?.lineCount),
  recordedCount: num(raw?.recordedCount),
  hasEntryForDate: bool(raw?.hasEntryForDate),
  isMonitored: bool(raw?.isMonitored),
  pendingMaterialCount: num(raw?.pendingMaterialCount),
  hasPendingMaterial: bool(raw?.hasPendingMaterial),
  pendingEquipmentCount: num(raw?.pendingEquipmentCount),
  pendingCostHeadCount: num(raw?.pendingCostHeadCount),
  producedTillDate: str(raw?.producedTillDate),
  pendingMaterialDates: (raw?.pendingMaterialDates || []).map((day: any) => ({
    workDate: str(day?.workDate),
    producedQty: str(day?.producedQty),
    lineCount: num(day?.lineCount),
    bookedTodayCount: num(day?.bookedTodayCount),
  })),
  pendingEquipmentDates: (raw?.pendingEquipmentDates || []).map((day: any) => ({
    workDate: str(day?.workDate),
    producedQty: str(day?.producedQty),
    lineCount: num(day?.lineCount),
    bookedTodayCount: num(day?.bookedTodayCount),
  })),
  pendingCostHeadDates: (raw?.pendingCostHeadDates || []).map((day: any) => ({
    workDate: str(day?.workDate),
    producedQty: str(day?.producedQty),
    lineCount: num(day?.lineCount),
    bookedTodayCount: num(day?.bookedTodayCount),
  })),
});

export const normaliseDaySheet = (raw: any): DaySheet => ({
  projectId: num(raw?.projectId),
  date: str(raw?.date),
  scope: raw?.scope === "MONITORED" ? "MONITORED" : "ALL",
  monitoringConfigured: bool(raw?.monitoringConfigured),
  taskCount: num(raw?.taskCount),
  recordedTaskCount: num(raw?.recordedTaskCount),
  dayLocked: bool(raw?.dayLocked),
  phases: (raw?.phases || []).map((phase: any) => ({
    phaseId: num(phase?.phaseId),
    phaseName: str(phase?.phaseName),
    taskCount: num(phase?.taskCount),
    recordedTaskCount: num(phase?.recordedTaskCount),
    activities: (phase?.activities || []).map((activity: any) => ({
      activityId: num(activity?.activityId),
      activityName: str(activity?.activityName),
      activitySequenceId: str(activity?.activitySequenceId),
      taskCount: num(activity?.taskCount),
      recordedTaskCount: num(activity?.recordedTaskCount),
      tasks: (activity?.tasks || []).map(normaliseTask),
    })),
  })),
});

const rateSource = (value: unknown): RateSource =>
  value === "MOVING_AVG" || value === "NORM" || value === "MASTER" || value === "ENTERED"
    ? value
    : "";

export const normaliseMaterialSheet = (raw: any): MaterialSheet => ({
  taskId: num(raw?.taskId),
  taskName: str(raw?.taskName),
  taskSequenceId: str(raw?.taskSequenceId),
  issueDate: str(raw?.issueDate),
  producedQty: str(raw?.producedQty),
  producedTillDate: str(raw?.producedTillDate),
  workUomName: str(raw?.workUomName),
  hasEntryForDate: bool(raw?.hasEntryForDate),
  lines: (raw?.lines || []).map((line: any) => ({
    taskBoqId: num(line?.taskBoqId),
    materialId: num(line?.materialId),
    materialName: str(line?.materialName),
    productCode: str(line?.productCode),
    categoryId: num(line?.categoryId),
    categoryName: str(line?.categoryName),
    uomName: str(line?.uomName),
    uomId: num(line?.uomId),
    allottedQuantity: str(line?.allottedQuantity),
    qtyIssuedToday: str(line?.qtyIssuedToday),
    qtyIssuedTillDate: str(line?.qtyIssuedTillDate),
    remarks: str(line?.remarks),
    rate: str(line?.rate),
    rateSource: rateSource(line?.rateSource),
    suggestedRate: str(line?.suggestedRate),
    suggestedRateSource: rateSource(line?.suggestedRateSource),
    normRate: str(line?.normRate),
    normQtyPerUnit: str(line?.normQtyPerUnit),
    targetQtyToday: str(line?.targetQtyToday),
    // The norm's ABS columns. wastage is the stored FRACTION — 0.02 is 2%.
    pucFactor: str(line?.pucFactor),
    wastage: str(line?.wastage),
    pendingQty: str(line?.pendingQty),
    pendingByDate: (line?.pendingByDate || []).map((day: any) => ({
      workDate: str(day?.workDate),
      producedQty: str(day?.producedQty),
      requiredQty: str(day?.requiredQty),
      pendingQty: str(day?.pendingQty),
    })),
    civilDays: (line?.civilDays || []).map((day: any) => ({
      workDate: str(day?.workDate),
      producedQty: str(day?.producedQty),
      requiredQty: str(day?.requiredQty),
      bookedQty: str(day?.bookedQty),
      bookedTodayQty: str(day?.bookedTodayQty),
      pendingQty: str(day?.pendingQty),
      bookedAmount: str(day?.bookedAmount),
      bookedPricedQty: str(day?.bookedPricedQty),
    })),
    targetQtyTillDate: str(line?.targetQtyTillDate),
    amountIssuedTillDate: str(line?.amountIssuedTillDate),
  })),
});

const normaliseCategoryTotal = (raw: any): MaterialCategoryTotal => ({
  categoryId: num(raw?.categoryId),
  categoryName: str(raw?.categoryName),
  lineCount: num(raw?.lineCount),
  taskCount: num(raw?.taskCount),
  targetAmount: str(raw?.targetAmount),
  actualAmount: str(raw?.actualAmount),
  targetPerUnit: str(raw?.targetPerUnit),
  actualPerUnit: str(raw?.actualPerUnit),
});

const targetState = (value: unknown): StockTargetState =>
  value === "OVER" || value === "WITHIN" || value === "NO_TARGET" ? value : "";

const varianceState = (value: unknown): StockVarianceState =>
  value === "MATCH" ||
  value === "STORE_HIGHER" ||
  value === "STORE_LOWER" ||
  value === "NO_TASK_ISSUE" ||
  value === "UNIT_MISMATCH"
    ? value
    : "";

export const normaliseStockSheet = (raw: any): StockSheet => ({
  projectId: num(raw?.projectId),
  date: str(raw?.date),
  dayLocked: bool(raw?.dayLocked),
  materialCount: num(raw?.materialCount),
  movementCount: num(raw?.movementCount),
  mismatchCount: num(raw?.mismatchCount),
  overNormCount: num(raw?.overNormCount),
  overTargetCount: num(raw?.overTargetCount),
  negativeCount: num(raw?.negativeCount),
  openingValue: str(raw?.openingValue),
  purchasedValue: str(raw?.purchasedValue),
  issuedValue: str(raw?.issuedValue),
  adjustmentValue: str(raw?.adjustmentValue),
  closingValue: str(raw?.closingValue),
  categories: listOf(raw?.categories).map((category: any) => ({
    categoryId: num(category?.categoryId),
    categoryName: str(category?.categoryName),
    materialCount: num(category?.materialCount),
    movementCount: num(category?.movementCount),
    openingValue: str(category?.openingValue),
    purchasedValue: str(category?.purchasedValue),
    issuedValue: str(category?.issuedValue),
    adjustmentValue: str(category?.adjustmentValue),
    closingValue: str(category?.closingValue),
  })),
  rows: listOf(raw?.rows).map((row: any) => ({
    materialId: num(row?.materialId),
    materialName: str(row?.materialName),
    productCode: str(row?.productCode),
    categoryId: num(row?.categoryId),
    categoryName: str(row?.categoryName),
    uomId: num(row?.uomId),
    uomName: str(row?.uomName),
    carriedFrom: str(row?.carriedFrom),
    isOpeningAnchor: bool(row?.isOpeningAnchor),
    openingQty: str(row?.openingQty),
    openingRate: str(row?.openingRate),
    openingValue: str(row?.openingValue),
    purchasedQty: str(row?.purchasedQty),
    purchasedRate: str(row?.purchasedRate),
    purchasedValue: str(row?.purchasedValue),
    issuedQty: str(row?.issuedQty),
    issuedRate: str(row?.issuedRate),
    issuedValue: str(row?.issuedValue),
    adjustmentQty: str(row?.adjustmentQty),
    adjustmentValue: str(row?.adjustmentValue),
    adjustmentReason: str(row?.adjustmentReason),
    remarks: str(row?.remarks),
    avgRate: str(row?.avgRate),
    closingQty: str(row?.closingQty),
    closingValue: str(row?.closingValue),
    taskIssuedQty: str(row?.taskIssuedQty),
    taskIssuedAmount: str(row?.taskIssuedAmount),
    taskIssuedRate: str(row?.taskIssuedRate),
    varianceQty: str(row?.varianceQty),
    varianceState: varianceState(row?.varianceState),
    hasRow: bool(row?.hasRow),
    normRate: str(row?.normRate),
    purchaseAboveNorm: bool(row?.purchaseAboveNorm),
    targetQty: str(row?.targetQty),
    targetTaskCount: num(row?.targetTaskCount),
    targetState: targetState(row?.targetState),
  })),
});

export const normaliseStockRegister = (raw: any): StockRegister => ({
  projectId: num(raw?.projectId),
  fromDate: str(raw?.fromDate),
  toDate: str(raw?.toDate),
  dayCount: num(raw?.dayCount),
  rowCount: num(raw?.rowCount),
  purchasedValue: str(raw?.purchasedValue),
  issuedValue: str(raw?.issuedValue),
  adjustmentValue: str(raw?.adjustmentValue),
  closingValue: str(raw?.closingValue),
  overNormCount: num(raw?.overNormCount),
  overTargetCount: num(raw?.overTargetCount),
  mismatchCount: num(raw?.mismatchCount),
  negativeCount: num(raw?.negativeCount),
  days: listOf(raw?.days).map((day: any) => ({
    date: str(day?.date),
    materialCount: num(day?.materialCount),
    overNormCount: num(day?.overNormCount),
    overTargetCount: num(day?.overTargetCount),
    mismatchCount: num(day?.mismatchCount),
    negativeCount: num(day?.negativeCount),
    openingValue: str(day?.openingValue),
    purchasedValue: str(day?.purchasedValue),
    issuedValue: str(day?.issuedValue),
    adjustmentValue: str(day?.adjustmentValue),
    closingValue: str(day?.closingValue),
    rows: listOf(day?.rows).map((row: any) => ({
      materialId: num(row?.materialId),
      materialName: str(row?.materialName),
      productCode: str(row?.productCode),
      categoryId: num(row?.categoryId),
      categoryName: str(row?.categoryName),
      uomName: str(row?.uomName),
      openingQty: str(row?.openingQty),
      openingValue: str(row?.openingValue),
      purchasedQty: str(row?.purchasedQty),
      purchasedRate: str(row?.purchasedRate),
      purchasedValue: str(row?.purchasedValue),
      issuedQty: str(row?.issuedQty),
      issuedRate: str(row?.issuedRate),
      issuedValue: str(row?.issuedValue),
      adjustmentQty: str(row?.adjustmentQty),
      adjustmentValue: str(row?.adjustmentValue),
      adjustmentReason: str(row?.adjustmentReason),
      closingQty: str(row?.closingQty),
      closingValue: str(row?.closingValue),
      avgRate: str(row?.avgRate),
      remarks: str(row?.remarks),
      normRate: str(row?.normRate),
      purchaseAboveNorm: bool(row?.purchaseAboveNorm),
      targetQty: str(row?.targetQty),
      targetTaskCount: num(row?.targetTaskCount),
      targetState: targetState(row?.targetState),
      taskIssuedQty: str(row?.taskIssuedQty),
      varianceQty: str(row?.varianceQty),
      varianceState: varianceState(row?.varianceState),
    })),
  })),
});

export const normaliseMaterialCategorySummary = (raw: any): MaterialCategorySummary => ({
  projectId: num(raw?.projectId),
  date: str(raw?.date),
  scope: raw?.scope === "MONITORED" ? "MONITORED" : "ALL",
  monitoringConfigured: bool(raw?.monitoringConfigured),
  taskCount: num(raw?.taskCount),
  categories: (raw?.categories || []).map(normaliseCategoryTotal),
  tasks: (raw?.tasks || []).map((task: any) => ({
    taskId: num(task?.taskId),
    categories: (task?.categories || []).map(normaliseCategoryTotal),
    targetAmount: str(task?.targetAmount),
    actualAmount: str(task?.actualAmount),
    producedQty: str(task?.producedQty),
    workUomName: str(task?.workUomName),
  })),
  targetAmount: str(raw?.targetAmount),
  actualAmount: str(raw?.actualAmount),
  producedQty: str(raw?.producedQty),
  perUnitWorkUomName: str(raw?.perUnitWorkUomName),
});

export const emptyHeadHours = (): EquipmentHeadHours => ({
  depreciation: "",
  interest: "",
  repair: "",
  misc: "",
  insurance: "",
  electricity: "",
  fuel: "",
  lubricants: "",
});

export const normaliseEquipmentSheet = (raw: any): EquipmentSheet => ({
  taskId: num(raw?.taskId),
  taskName: str(raw?.taskName),
  taskSequenceId: str(raw?.taskSequenceId),
  logDate: str(raw?.logDate),
  producedQty: str(raw?.producedQty),
  producedTillDate: str(raw?.producedTillDate),
  workUomName: str(raw?.workUomName),
  hasEntryForDate: bool(raw?.hasEntryForDate),
  uomOptions: (raw?.uomOptions || []).map((u: any) => ({
    uomId: num(u?.uomId),
    uomName: str(u?.uomName),
    uomShortCode: str(u?.uomShortCode),
  })),
  lines: (raw?.lines || []).map((line: any) => {
    const heads = emptyHeadHours();
    EQUIPMENT_HEAD_KEYS.forEach((key) => {
      heads[key] = str(line?.headHours?.[key]);
    });
    const fixedByKey = new Map<string, any>((line?.fixedHeads || []).map((h: any) => [str(h?.head), h]));
    const variableByKey = new Map<string, any>((line?.variableHeads || []).map((h: any) => [str(h?.head), h]));
    return {
      assetId: num(line?.assetId),
      assetCode: str(line?.assetCode),
      assetName: str(line?.assetName),
      toolTypeName: str(line?.toolTypeName),
      plannedHours: str(line?.plannedHours),
      pendingHours: str(line?.pendingHours),
      targetHoursTillDate: str(line?.targetHoursTillDate),
      civilDays: (line?.civilDays || []).map((day: any) => ({
        workDate: str(day?.workDate),
        producedQty: str(day?.producedQty),
        requiredQty: str(day?.requiredQty),
        bookedQty: str(day?.bookedQty),
        bookedTodayQty: str(day?.bookedTodayQty),
        pendingQty: str(day?.pendingQty),
        bookedAmount: "",
        bookedPricedQty: "",
        earlierHeads: (day?.earlierHeads || []).map((h: any) => ({
          head: str(h?.head),
          qty: str(h?.qty),
          uomName: str(h?.uomName),
          rate: str(h?.rate),
          amount: str(h?.amount),
          logDate: str(h?.logDate),
          hoursHere: str(h?.hoursHere),
          logHours: str(h?.logHours),
        })),
      })),
      hoursWorked: str(line?.hoursWorked),
      fuelLitres: str(line?.fuelLitres),
      readingStart: str(line?.readingStart),
      readingEnd: str(line?.readingEnd),
      isBreakdown: bool(line?.isBreakdown),
      idleReason: str(line?.idleReason),
      hoursTillDate: str(line?.hoursTillDate),
      lastReadingEnd: str(line?.lastReadingEnd),
      prodNormPerHour: str(line?.prodNormPerHour),
      nonProdFactor: str(line?.nonProdFactor),
      utilizationFactor: str(line?.utilizationFactor),
      applicationFactor: str(line?.applicationFactor),
      prodNormUomName: str(line?.prodNormUomName),
      normLineCount: num(line?.normLineCount),
      headHours: heads,
      // Always all three / all five, in canonical order, defaulted — the form
      // renders a fixed layout and never asks "is this head present".
      fixedHeads: FIXED_HEAD_KEYS.map((head) => {
        const h = fixedByKey.get(head);
        return { head, label: str(h?.label) || head, ratePerHour: str(h?.ratePerHour), amount: str(h?.amount) };
      }),
      variableHeads: VARIABLE_HEAD_KEYS.map((head) => {
        const h = variableByKey.get(head);
        const rateSource = str(h?.rateSource);
        return {
          head,
          label: str(h?.label) || head,
          qty: str(h?.qty),
          uomId: num(h?.uomId),
          uomName: str(h?.uomName),
          rate: str(h?.rate),
          amount: str(h?.amount),
          rateSource: (rateSource === "ENTERED" || rateSource === "MASTER" ? rateSource : "") as "ENTERED" | "MASTER" | "",
          defaultUomId: num(h?.defaultUomId),
          defaultUomName: str(h?.defaultUomName),
          defaultRate: str(h?.defaultRate),
          normRatePerHour: str(h?.normRatePerHour),
        };
      }),
      rateStatus: str(line?.rateStatus) === "NO_RATE" ? "NO_RATE" : "",
    };
  }),
});

// ------------------------------------------------------------------ calls
export interface DaySheetQuery {
  date: string;
  scope?: DaySheetScope;
  phaseId?: number | string;
  activityId?: number | string;
  // Only tasks the Civil section produced on the date (execution-ledger row
  // exists). Material issue is consumption against that output.
  producedOnly?: boolean;
  // Material sheet: also tasks still owed material from earlier civil work.
  includePending?: boolean;
  // Every civil-completed task up to the date (booking is by task, not day).
  civilDone?: boolean;
}

const sheetParams = (filters: DaySheetQuery) => {
  const params: Record<string, any> = { date: filters.date };
  (["scope", "phaseId", "activityId"] as const).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== "") {
      params[key] = value;
    }
  });
  if (filters.producedOnly) params.producedOnly = "true";
  if (filters.includePending) params.includePending = "true";
  if (filters.civilDone) params.civilDone = "true";
  return params;
};

// ------------------------------------------------------ execution calendar
export type CalendarDayState =
  | "approved"
  | "review"
  | "rejected"
  | "entered"
  | "notEntered"
  | "nonWorking"
  | "today"
  | "future"
  | "beforeStart";
export type CalendarSection = "all" | "civil" | "material" | "mechanical";

export interface CalendarAdvance {
  slotLabel: string;
  qty: string;
  uomName: string;
  hasStretches: boolean;
}

export interface ExecutionCalendarDay {
  date: string;
  state: CalendarDayState;
  isWorkingDay: boolean;
  holidayName: string;
  isToday: boolean;
  entryCount: number;
  faceCount: number;
  boqLineCount: number;
  advance: CalendarAdvance[];
  materialTaskCount: number;
  materialLineCount: number;
  machineCount: number;
  machineHours: string;
  approvalStatus: string;
  approvalLevel: number;
  approvalLevelCount: number;
  openNoteCount: number;
  hasCivil: boolean;
  hasMaterial: boolean;
  hasMechanical: boolean;
}

export interface ExecutionCalendarMonth {
  projectId: number;
  month: string;
  projectStart: string;
  calendarTitle: string;
  section: CalendarSection;
  today: string;
  monitoringConfigured: boolean;
  days: ExecutionCalendarDay[];
  summary: {
    workingDaysSoFar: number;
    enteredDays: number;
    notEnteredDays: number;
    inReviewDays: number;
    approvedDays: number;
    rejectedDays: number;
    enteredPercent: string;
  };
}

export interface CalendarDayTask {
  taskId: number;
  taskSequenceId: string;
  taskName: string;
  activityId: number;
  activityName: string;
  lineCount: number;
  recordedCount: number;
  producedQty: string;
  workUomName: string;
}

export interface CalendarDayEntry {
  entryId: number;
  activityId: number;
  activityName: string;
  phaseName: string;
  shiftId: number;
  shiftName: string;
  startTime: string;
  endTime: string;
  boqLineCount: number;
  advance: CalendarAdvance[];
}

export interface CalendarDayDetail {
  projectId: number;
  date: string;
  day: ExecutionCalendarDay;
  monitoringConfigured: boolean;
  entries: CalendarDayEntry[];
  trackedNotCaptured: CalendarDayTask[];
  facesNotCaptured: Array<{ activityId: number; activityName: string; trackedTaskCount: number }>;
  materialRecorded: CalendarDayTask[];
  materialMissing: CalendarDayTask[];
  mechanicalRecorded: CalendarDayTask[];
  mechanicalMissing: CalendarDayTask[];
}

const CALENDAR_STATES: CalendarDayState[] = [
  "approved", "review", "rejected", "entered", "notEntered", "nonWorking", "today", "future", "beforeStart",
];
const listOf = (value: unknown): any[] => (Array.isArray(value) ? value : []);
const normaliseAdvance = (raw: any): CalendarAdvance => ({
  slotLabel: str(raw?.slotLabel),
  qty: str(raw?.qty),
  uomName: str(raw?.uomName),
  hasStretches: bool(raw?.hasStretches),
});

export const normaliseCalendarDay = (raw: any): ExecutionCalendarDay => ({
  date: str(raw?.date),
  state: (CALENDAR_STATES.includes(raw?.state) ? raw.state : "future") as CalendarDayState,
  isWorkingDay: bool(raw?.isWorkingDay),
  holidayName: str(raw?.holidayName),
  isToday: bool(raw?.isToday),
  entryCount: num(raw?.entryCount),
  faceCount: num(raw?.faceCount),
  boqLineCount: num(raw?.boqLineCount),
  advance: listOf(raw?.advance).map(normaliseAdvance),
  materialTaskCount: num(raw?.materialTaskCount),
  materialLineCount: num(raw?.materialLineCount),
  machineCount: num(raw?.machineCount),
  machineHours: str(raw?.machineHours),
  approvalStatus: str(raw?.approvalStatus),
  approvalLevel: num(raw?.approvalLevel),
  approvalLevelCount: num(raw?.approvalLevelCount),
  openNoteCount: num(raw?.openNoteCount),
  hasCivil: bool(raw?.hasCivil),
  hasMaterial: bool(raw?.hasMaterial),
  hasMechanical: bool(raw?.hasMechanical),
});

export const normaliseCalendarMonth = (raw: any): ExecutionCalendarMonth => ({
  projectId: num(raw?.projectId),
  month: str(raw?.month),
  projectStart: str(raw?.projectStart),
  calendarTitle: str(raw?.calendarTitle),
  section: (["civil", "material", "mechanical"].includes(raw?.section) ? raw.section : "all") as CalendarSection,
  today: str(raw?.today),
  monitoringConfigured: bool(raw?.monitoringConfigured),
  days: listOf(raw?.days).map(normaliseCalendarDay),
  summary: {
    workingDaysSoFar: num(raw?.summary?.workingDaysSoFar),
    enteredDays: num(raw?.summary?.enteredDays),
    notEnteredDays: num(raw?.summary?.notEnteredDays),
    inReviewDays: num(raw?.summary?.inReviewDays),
    approvedDays: num(raw?.summary?.approvedDays),
    rejectedDays: num(raw?.summary?.rejectedDays),
    enteredPercent: str(raw?.summary?.enteredPercent),
  },
});

const normaliseDayTask = (raw: any): CalendarDayTask => ({
  taskId: num(raw?.taskId),
  taskSequenceId: str(raw?.taskSequenceId),
  taskName: str(raw?.taskName),
  activityId: num(raw?.activityId),
  activityName: str(raw?.activityName),
  lineCount: num(raw?.lineCount),
  recordedCount: num(raw?.recordedCount),
  producedQty: str(raw?.producedQty),
  workUomName: str(raw?.workUomName),
});

export const normaliseCalendarDayDetail = (raw: any): CalendarDayDetail => ({
  projectId: num(raw?.projectId),
  date: str(raw?.date),
  day: normaliseCalendarDay(raw?.day),
  monitoringConfigured: bool(raw?.monitoringConfigured),
  entries: listOf(raw?.entries).map((entry) => ({
    entryId: num(entry?.entryId),
    activityId: num(entry?.activityId),
    activityName: str(entry?.activityName),
    phaseName: str(entry?.phaseName),
    shiftId: num(entry?.shiftId),
    shiftName: str(entry?.shiftName),
    startTime: str(entry?.startTime),
    endTime: str(entry?.endTime),
    boqLineCount: num(entry?.boqLineCount),
    advance: listOf(entry?.advance).map(normaliseAdvance),
  })),
  trackedNotCaptured: listOf(raw?.trackedNotCaptured).map(normaliseDayTask),
  facesNotCaptured: listOf(raw?.facesNotCaptured).map((face) => ({
    activityId: num(face?.activityId),
    activityName: str(face?.activityName),
    trackedTaskCount: num(face?.trackedTaskCount),
  })),
  materialRecorded: listOf(raw?.materialRecorded).map(normaliseDayTask),
  materialMissing: listOf(raw?.materialMissing).map(normaliseDayTask),
  mechanicalRecorded: listOf(raw?.mechanicalRecorded).map(normaliseDayTask),
  mechanicalMissing: listOf(raw?.mechanicalMissing).map(normaliseDayTask),
});

// ==================== Mucking / PRW cost heads ====================

/** Which group of the client's ABS cost abstract a line belongs to. */
export type NormHead = "MUCKING" | "PRW";

/**
 * One outsourced cost head for one task-day.
 *
 * Target comes from the norm applied to the task — its quantity per unit of
 * output times what the task produced today, at the rate frozen on the
 * application. '' is "nothing to state" and is never 0: an unpriced norm has no
 * target amount, and a day with no production has no target quantity.
 */
export interface NormActualLine {
  head: NormHead;
  normId: number;
  applicationId: number;
  itemCode: string;
  itemName: string;
  uomId: number;
  uomName: string;
  /** Mucking only: which dump, and how far. */
  nearestDump: string;
  totalLead: string;
  normQtyPerUnit: string;
  targetQty: string;
  targetRate: string;
  targetAmount: string;
  actualQty: string;
  actualRate: string;
  actualAmount: string;
  variancePercent: string;
  rateAboveNorm: boolean;
  remarks: string;
  // Quantity still owed to the task's civil work already done; '' = none.
  pendingQty: string;
  // Every civil day with its target, booked (and this date's own entry) and
  // owed, plus the money booked — the date-wise view.
  civilDays: MaterialCivilDay[];
}

export interface NormActualSheet {
  taskId: number;
  taskName: string;
  taskSequenceId: string;
  actualDate: string;
  producedQty: string;
  workUomName: string;
  dayLocked: boolean;
  lines: NormActualLine[];
  hasEntryForDate: boolean;
}

export interface NormActualSaveRow {
  head: NormHead;
  normId: number;
  qty?: string;
  rate?: string;
  remarks?: string;
  // How much of qty was entered against which civil date.
  civilDates?: Array<{ civilDate: string; qty: string }>;
}

export const normaliseNormActuals = (raw: any): NormActualSheet => ({
  taskId: num(raw?.taskId),
  taskName: str(raw?.taskName),
  taskSequenceId: str(raw?.taskSequenceId),
  actualDate: str(raw?.actualDate),
  producedQty: str(raw?.producedQty),
  workUomName: str(raw?.workUomName),
  dayLocked: bool(raw?.dayLocked),
  lines: listOf(raw?.lines).map((line: any) => ({
    head: (str(line?.head) || "MUCKING") as NormHead,
    normId: num(line?.normId),
    applicationId: num(line?.applicationId),
    itemCode: str(line?.itemCode),
    itemName: str(line?.itemName),
    uomId: num(line?.uomId),
    uomName: str(line?.uomName),
    nearestDump: str(line?.nearestDump),
    totalLead: str(line?.totalLead),
    normQtyPerUnit: str(line?.normQtyPerUnit),
    targetQty: str(line?.targetQty),
    targetRate: str(line?.targetRate),
    targetAmount: str(line?.targetAmount),
    actualQty: str(line?.actualQty),
    actualRate: str(line?.actualRate),
    actualAmount: str(line?.actualAmount),
    variancePercent: str(line?.variancePercent),
    rateAboveNorm: bool(line?.rateAboveNorm),
    remarks: str(line?.remarks),
    pendingQty: str(line?.pendingQty),
    civilDays: (line?.civilDays || []).map((day: any) => ({
      workDate: str(day?.workDate),
      producedQty: str(day?.producedQty),
      requiredQty: str(day?.requiredQty),
      bookedQty: str(day?.bookedQty),
      bookedTodayQty: str(day?.bookedTodayQty),
      pendingQty: str(day?.pendingQty),
      bookedAmount: str(day?.bookedAmount),
      bookedPricedQty: str(day?.bookedPricedQty),
    })),
  })),
  hasEntryForDate: bool(raw?.hasEntryForDate),
});

export const siteDailyApis = {
  // Phase → activity → task tree of the tasks that can take material issues
  // on `date`. When the project has no tracking configured the server applies
  // ALL and answers `monitoringConfigured: false`.
  materialTasks: (projectId: number | string, filters: DaySheetQuery): Promise<any> =>
    makeApiCall({
      url: `/site-daily/materials/tasks/${projectId}`,
      method: "get",
      params: sheetParams(filters),
    }),

  equipmentTasks: (projectId: number | string, filters: DaySheetQuery): Promise<any> =>
    makeApiCall({
      url: `/site-daily/equipment/tasks/${projectId}`,
      method: "get",
      params: sheetParams(filters),
    }),

  // The same day rolled up by material category — target against actual, for the
  // day and per task. Its task set is the sheet's own (producedOnly is implied),
  // so the rollup always describes exactly the tasks the tree shows.
  materialCategorySummary: (
    projectId: number | string,
    filters: Omit<DaySheetQuery, "producedOnly">
  ): Promise<any> =>
    makeApiCall({
      url: `/site-daily/materials/category-summary/${projectId}`,
      method: "get",
      params: sheetParams(filters),
    }),

  // The project store's priced ledger for one day. Project-scoped: no task, no
  // tracking scope — a store holds material for the whole project.
  stockSheet: (
    projectId: number | string,
    params: { date: string; categoryId?: number }
  ): Promise<any> =>
    makeApiCall({
      url: `/site-daily/stock/${projectId}`,
      method: "get",
      params: params.categoryId
        ? { date: params.date, categoryId: params.categoryId }
        : { date: params.date },
    }),

  // FULL replace-set per project + date. A material with no movement is simply
  // absent — its balance keeps carrying from the last day that had one — so send
  // only the rows that were actually touched.
  saveStock: (stock: {
    projectId: number;
    stockDate: string;
    rows: StockSaveRow[];
  }): Promise<any> =>
    makeApiCall({ url: `/site-daily/stock/save`, method: "post", data: stock }),

  // The same ledger day by day. Only days with movement come back.
  stockRegister: (
    projectId: number | string,
    params: { fromDate: string; toDate: string }
  ): Promise<any> =>
    makeApiCall({
      url: `/site-daily/stock/register/${projectId}`,
      method: "get",
      params,
    }),

  uploadStockExcel: (formData: FormData): Promise<any> =>
    makeApiCall({
      url: `/site-daily/stock/upload/excel`,
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // The two outsourced heads of the ABS abstract for one task-day.
  costHeads: (taskId: number | string, params: { date: string }): Promise<any> =>
    makeApiCall({ url: `/site-daily/cost-heads/${taskId}`, method: "get", params }),

  // FULL replace-set per task + date. A head the site clears is simply absent,
  // which is how "nothing was outsourced today" is stated.
  saveCostHeads: (actuals: {
    taskId: number;
    actualDate: string;
    rows: NormActualSaveRow[];
    // true = the rows' civilDates are the day's full per-date split.
    byCivilDate?: boolean;
  }): Promise<any> =>
    makeApiCall({ url: `/site-daily/cost-heads/save`, method: "post", data: actuals }),

  getMaterials: (taskId: number | string, date: string): Promise<any> =>
    makeApiCall({
      url: `/site-daily/materials`,
      method: "get",
      params: { taskId, date },
    }),

  // FULL replace-set per task + date. Lines with '' or '0' qty are dropped
  // server-side, so send every line that has a qty.
  saveMaterials: (payload: {
    taskId: number;
    issueDate: string;
    lines: MaterialSaveLine[];
    // true = the lines' civilDates are the day's full per-date split.
    byCivilDate?: boolean;
  }): Promise<any> =>
    makeApiCall({
      url: `/site-daily/materials/save`,
      method: "post",
      data: payload,
    }),

  // Excel uploads (validate -> preview -> commit on the shared upload engine).
  // Each task-day in the file is saved as the sheet saves it — a replace-set.
  uploadMaterialsExcel: (formData: FormData): Promise<any> =>
    makeApiCall({
      url: `/site-daily/materials/upload/excel`,
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  uploadEquipmentExcel: (formData: FormData): Promise<any> =>
    makeApiCall({
      url: `/site-daily/equipment/upload/excel`,
      method: "post",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  // Execution Calendar — a month of days classified, and one day's drill-down.
  calendarMonth: (
    projectId: number | string,
    filters: { month: string; phaseId?: number; activityId?: number; section?: CalendarSection }
  ): Promise<any> => {
    const params: Record<string, any> = { month: filters.month };
    if (filters.phaseId) params.phaseId = filters.phaseId;
    if (filters.activityId) params.activityId = filters.activityId;
    if (filters.section && filters.section !== "all") params.section = filters.section;
    return makeApiCall({ url: `/site-daily/calendar/${projectId}`, method: "get", params });
  },

  calendarDay: (
    projectId: number | string,
    filters: { date: string; phaseId?: number; activityId?: number }
  ): Promise<any> => {
    const params: Record<string, any> = { date: filters.date };
    if (filters.phaseId) params.phaseId = filters.phaseId;
    if (filters.activityId) params.activityId = filters.activityId;
    return makeApiCall({ url: `/site-daily/calendar/${projectId}/day`, method: "get", params });
  },

  getEquipment: (taskId: number | string, date: string): Promise<any> =>
    makeApiCall({
      url: `/site-daily/equipment`,
      method: "get",
      params: { taskId, date },
    }),

  // Replace-set; a line with nothing recorded is dropped server-side.
  saveEquipment: (payload: {
    taskId: number;
    logDate: string;
    lines: EquipmentSaveLine[];
    // true = the lines' civilDates are the day's full per-date split.
    byCivilDate?: boolean;
  }): Promise<any> =>
    makeApiCall({
      url: `/site-daily/equipment/save`,
      method: "post",
      data: payload,
    }),
};
